import { Heading, Icon, Text } from "@oysterhr/ui"
import classNames from "classnames"
import { ReactNode } from "react"

export type PendingItem = {
  step: number
  taskName: string
  loading?: boolean
  disabled?: boolean
  action?: ReactNode
}

export type CompletedItem = {
  taskName: string
}

type TasksQueueProps = {
  pendingTasks: PendingItem[]
  completedTasks: CompletedItem[]
}

// TODO: We can reuse TasksQueue component with a few adjustments. I will create this as a temporary solution until I get their approval on the changes since we're using mocked data
export function PendingActions({ pendingTasks, completedTasks }: TasksQueueProps) {
  return (
    <div className="rounded-lg bg-gradient-delightful p-4">
      <div className="mb-4 flex flex-col justify-center">
        <Heading size="xl" className="font-semibold text-black">
          {`Pending steps (${pendingTasks.length})`}
        </Heading>

        <Text className="text-gray-400" size="small">
          Complete these steps to fully set up your FTE payment method
        </Text>
      </div>

      <div className="space-y-6">
        <div>
          {pendingTasks.length > 0 ? (
            <ul className="space-y-4">
              {pendingTasks.map((task, index) => (
                <PendingTaskItem
                  key={task.step}
                  step={task.step}
                  loading={task.loading}
                  taskName={task.taskName}
                  action={task.action}
                  disabled={index > 0}
                />
              ))}
            </ul>
          ) : null}
        </div>
        <div>
          <Heading size="xl" className="font-semibold text-black">
            Completed steps
          </Heading>
          {completedTasks.length > 0 ? (
            <ul className="space-y-2 pt-4">
              {completedTasks.map((task, index) => (
                <CompletedTaskItem key={`completed-item-${index}`} taskName={task.taskName} />
              ))}
            </ul>
          ) : (
            <span className="text-sm text-black">No tasks</span>
          )}
        </div>
      </div>
    </div>
  )
}

type PendingTaskItemProps = PendingItem

function PendingTaskItem({
  step,
  taskName,
  action,
  loading,
  disabled = true,
}: PendingTaskItemProps) {
  return (
    <li className="flex flex-row items-center space-x-4 rounded-lg bg-white p-3">
      <div
        className={classNames("flex flex-1 items-center space-x-2", {
          "text-gray-300": disabled,
        })}
      >
        {loading ? (
          <Icon name="SpinnerGap" size="sm" />
        ) : (
          <div
            className={classNames("h-6 w-6 rounded-full border border-gray-200 text-center", {
              "bg-gray-200": disabled,
            })}
          >
            {step}
          </div>
        )}
        <span>{taskName}</span>
      </div>
      <div className="flex-shrink-0">{action}</div>
    </li>
  )
}

type CompletedTaskItemProps = CompletedItem

function CompletedTaskItem({ taskName }: CompletedTaskItemProps) {
  return (
    <li className="space-x-2 rounded-lg bg-white p-3">
      <div className="flex flex-1 space-x-2">
        <Icon
          name="CheckCircle"
          size="sm"
          className="flex-shrink-0 self-center text-success-base"
        />
        <span className="flex-1">{taskName}</span>
      </div>
    </li>
  )
}
